import {
	useConfirmOrderQuoteItemAttachmentUploadsMutation,
	useGenerateOrderQuoteItemAttachmentUploadUrlMutation,
	useUpdateOrderQuoteItemAttachmentMutation,
} from '@codezo/graphql';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { prepareFiles } from '../../../lib/file-size';
import { getMutationError } from '../../../lib/mutation-response';
import { AnimatedHeight } from '../../ui/animated-height';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { FileInput } from '../../ui/file-input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import type { QuoteItemFile } from '../QuoteItemFiles';

/**
 * The one place a file's description is written — on the way up, and afterwards.
 *
 * The dialog OWNS the whole upload, picker included: Upload opens this, files are
 * chosen or dropped in here, and each one gets its description beside it before
 * anything is sent. Going out to the OS picker first and explaining afterwards
 * put the admin in a dialog they never asked for, holding files already chosen.
 *
 * The description is the point of the detour: `logo-v3-FINAL-2.ai` says nothing
 * about which placement it is for or what it supersedes, and the moment to say so
 * is while the admin still has it in mind. It is never required — a file can go
 * up bare and be explained later, which is what the edit mode is for.
 *
 * Descriptions are shown to CLIENTS, wherever the attachment is. The placeholder
 * says so; this is not the place for an internal note.
 */

/** Adding files to a product, or editing what one already says. */
type Mode = { kind: 'upload' } | { kind: 'edit'; file: QuoteItemFile };

const PLACEHOLDER = 'What this file is — the client sees this';

function formatSize(size: number) {
	return size < 1024 * 1024
		? `${(size / 1024).toFixed(0)} KB`
		: `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function QuoteItemFileModal({
	open,
	onOpenChange,
	itemId,
	mode,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	itemId: string;
	/** Null while the dialog is closing, so the body doesn't blank mid-animation. */
	mode: Mode | null;
	onSuccess: () => void;
}) {
	/* Picked here, not handed in: the dialog is the picker. Descriptions are
	   positional against this list — a picker can hand back two files of the same
	   name, so an index is the only stable key. */
	const [picked, setPicked] = useState<File[]>([]);
	const [descriptions, setDescriptions] = useState<string[]>([]);
	const [saving, setSaving] = useState(false);
	const [converting, setConverting] = useState(false);

	const [generateUrl] = useGenerateOrderQuoteItemAttachmentUploadUrlMutation();
	const [confirmUpload] = useConfirmOrderQuoteItemAttachmentUploadsMutation();
	const [updateAttachment] = useUpdateOrderQuoteItemAttachmentMutation();

	// Seed from the mode each time the dialog opens. Editing starts on what the
	// file already says, so a small correction isn't a retype; an upload starts
	// empty, with nothing picked yet.
	useEffect(() => {
		if (!open || !mode) return;
		setPicked([]);
		setDescriptions(mode.kind === 'edit' ? [mode.file.description ?? ''] : []);
	}, [open, mode]);

	/* Every route in — browse and drop — goes through `prepareFiles`, so HEIC
	   conversion and the size/type limits apply the same either way. */
	const addFiles = (incoming: File[]) => {
		if (!incoming.length) return;
		setPicked((prev) => [...prev, ...incoming]);
		setDescriptions((prev) => [...prev, ...incoming.map(() => '')]);
	};

	const handleDrop = async (e: React.DragEvent) => {
		e.preventDefault();
		const dropped = Array.from(e.dataTransfer.files);
		if (!dropped.length) return;
		setConverting(true);
		try {
			addFiles(await prepareFiles(dropped));
		} finally {
			setConverting(false);
		}
	};

	const removePicked = (idx: number) => {
		setPicked((prev) => prev.filter((_, i) => i !== idx));
		setDescriptions((prev) => prev.filter((_, i) => i !== idx));
	};

	const isEdit = mode?.kind === 'edit';
	const original = isEdit ? (mode.file.description ?? '') : '';
	const changed = isEdit ? descriptions[0]?.trim() !== original.trim() : picked.length > 0;

	const handleUpload = async (files: File[]) => {
		const urlResults = await Promise.all(
			files.map((file) =>
				generateUrl({
					variables: {
						input: {
							orderQuoteItemId: itemId,
							fileName: file.name,
							fileSize: file.size,
							fileType: file.type,
						},
					},
					fetchPolicy: 'no-cache',
				}).then(({ data }) => ({
					file,
					res: data?.generateOrderQuoteItemAttachmentUploadUrl,
				}))
			)
		);

		/* Descriptions are positional, so carry the original index through the
		   filter — dropping a file that failed to get a URL would otherwise shift
		   every description below it onto the wrong file. */
		const valid = urlResults
			.map((u, i) => ({ ...u, i }))
			.filter((u) => u.res?.success && u.res.uploadUrl && u.res.blobUrl && u.res.fileId);

		if (valid.length === 0) throw new Error('Could not start the upload');

		await Promise.all(
			valid.map(({ file, res }) =>
				fetch(res!.uploadUrl, {
					method: 'PUT',
					headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type },
					body: file,
				})
			)
		);

		const { data } = await confirmUpload({
			variables: {
				input: {
					orderQuoteItemId: itemId,
					files: valid.map(({ file, res, i }) => ({
						fileId: res!.fileId!,
						blobUrl: res!.blobUrl!,
						fileName: file.name,
						fileSize: file.size,
						fileType: file.type,
						// Omitted rather than sent empty: a file with nothing to say
						// should carry no description, not a blank one.
						description: descriptions[i]?.trim() || undefined,
					})),
				},
			},
		});
		const error = getMutationError(data?.confirmOrderQuoteItemAttachmentUploads);
		if (error) throw new Error(error);

		toast.success(`${valid.length} file${valid.length === 1 ? '' : 's'} uploaded`);
	};

	const handleEdit = async (file: QuoteItemFile) => {
		const { data } = await updateAttachment({
			variables: {
				orderQuoteItemId: itemId,
				attachmentId: file.id,
				// "" is not the same as omitted here: it is the only way to remove a
				// description that turned out to be wrong.
				input: { description: descriptions[0]?.trim() ?? '' },
			},
		});
		const error = getMutationError(data?.updateOrderQuoteItemAttachment);
		if (error) throw new Error(error);

		toast.success(descriptions[0]?.trim() ? 'Description saved' : 'Description removed');
	};

	const handleSubmit = async () => {
		if (!mode) return;
		setSaving(true);
		try {
			if (mode.kind === 'edit') await handleEdit(mode.file);
			else await handleUpload(picked);
			onSuccess();
			onOpenChange(false);
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: mode.kind === 'edit'
						? 'Failed to save the description'
						: 'Upload failed'
			);
		} finally {
			setSaving(false);
		}
	};

	const rows =
		mode?.kind === 'edit'
			? [
					{
						key: mode.file.id,
						name: mode.file.name,
						meta: formatSize(mode.file.size),
						i: 0,
						removable: false,
					},
				]
			: picked.map((file, i) => ({
					key: `${file.name}-${i}`,
					name: file.name,
					meta: formatSize(file.size),
					i,
					removable: true,
				}));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{isEdit ? 'Edit description' : 'Add files'}</DialogTitle>
					<DialogDescription>
						Shown on every offer for this product — the client reads it too.
					</DialogDescription>
				</DialogHeader>

				<DialogBody>
					<AnimatedHeight step={`${mode?.kind ?? 'none'}:${rows.length}`}>
						<div className="flex flex-col gap-4">
							{/* Only on upload — an edit is about one file that is already
							    here, and a dropzone would invite replacing it, which this
							    cannot do. */}
							{!isEdit && (
								<FileInput onFiles={addFiles} multiple>
									{({ open, converting: picking }) => {
										const busy = picking || converting;
										return (
											<div
												onDragOver={(e) => e.preventDefault()}
												onDrop={handleDrop}
												onClick={open}
												className="flex cursor-pointer flex-col items-center gap-2 rounded-[var(--r-2)] border-2 border-dashed border-line-1 bg-surface-fill/50 px-4 py-6 text-center transition-colors hover:border-line-2 hover:bg-surface-hover"
											>
												{busy ? (
													<Loader2
														size={18}
														className="animate-spin text-text-3"
													/>
												) : (
													<Upload size={18} className="text-text-3" />
												)}
												<span className="text-sm text-text-2">
													{busy
														? 'Converting…'
														: rows.length > 0
															? 'Add more files'
															: 'Drop files here, or click to browse'}
												</span>
											</div>
										);
									}}
								</FileInput>
							)}

							{rows.map((row) => (
								<div key={row.key} className="flex flex-col gap-1.5">
									<div className="flex items-center gap-2.5">
										<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--r-1)] border border-line-1 bg-surface-fill text-text-3">
											<FileText size={13} />
										</span>
										<span className="min-w-0 flex-1 truncate font-mono text-sm text-text-1">
											{row.name}
										</span>
										<span className="shrink-0 text-2xs text-text-4">
											{row.meta}
										</span>
										{row.removable && (
											<button
												type="button"
												title="Don't upload this one"
												onClick={() => removePicked(row.i)}
												className="shrink-0 cursor-pointer rounded p-1 text-text-4 transition-colors hover:text-status-danger"
											>
												<X size={12} />
											</button>
										)}
									</div>
									<Label className="sr-only">Description</Label>
									<Textarea
										value={descriptions[row.i] ?? ''}
										onChange={(e) =>
											setDescriptions((prev) => {
												const next = [...prev];
												next[row.i] = e.target.value;
												return next;
											})
										}
										placeholder={PLACEHOLDER}
										rows={2}
									/>
								</div>
							))}
						</div>
					</AnimatedHeight>
				</DialogBody>

				<DialogFooter>
					{/* Never a gate. A file can go up bare and be explained later — the
					    edit mode exists precisely so the upload doesn't have to wait on
					    someone finding the words. */}
					<span className="mr-auto text-xs text-text-4">
						{isEdit ? 'Leave empty to remove it.' : 'Optional — you can add it later.'}
					</span>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
						Cancel
					</Button>
					{/* `changed` covers both: an edit needs different text, an upload
					    needs at least one file. */}
					<Button onClick={handleSubmit} disabled={saving || !changed}>
						{saving ? (
							<>
								<Loader2 size={14} className="animate-spin" />
								{isEdit ? 'Saving…' : 'Uploading…'}
							</>
						) : isEdit ? (
							'Save'
						) : rows.length === 1 ? (
							'Upload file'
						) : (
							`Upload ${rows.length} files`
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export type { Mode as QuoteItemFileModalMode };
